import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SystemDomainService } from '../../../../services/system-domain.service';
import { ToastService } from '../../../../services/toast-service';
import { TranslateService } from '@ngx-translate/core';

interface OAuthField {
    key: string;
    label: string;
    type: string;
    required: boolean;
    placeholder?: string;
    order: number;
    helpText?: string;
}

@Component({
    selector: 'app-oauth-settings-modal',
    templateUrl: './oauth-settings-modal.component.html',
    styleUrls: ['./oauth-settings-modal.component.scss']
})
export class OAuthSettingsModalComponent implements OnInit {
    @Input() domainId!: number;
    @Input() domainName!: string;
    @Input() databaseName?: string;

    oauthForm!: FormGroup;
    loading = false;
    saving = false;
    testing = false;
    testResult: any = null;
    existingMappingId: number | null = null;

    fields: OAuthField[] = [
        {
            key: 'integrationName',
            label: 'OAuth.integrationName',
            type: 'text',
            required: true,
            placeholder: 'OAuth.integrationNamePlaceholder',
            order: 1,
            helpText: 'OAuth.integrationNameHelp'
        },
        {
            key: 'description',
            label: 'OAuth.description',
            type: 'textarea',
            required: false,
            placeholder: 'OAuth.descriptionPlaceholder',
            order: 2
        },
        {
            key: 'azureTenantId',
            label: 'OAuth.azureTenantId',
            type: 'text',
            required: true,
            placeholder: 'OAuth.azureTenantIdPlaceholder',
            order: 3,
            helpText: 'OAuth.azureTenantIdHelp'
        },
        {
            key: 'azureClientId',
            label: 'OAuth.azureClientId',
            type: 'text',
            required: true,
            placeholder: 'OAuth.azureClientIdPlaceholder',
            order: 4,
            helpText: 'OAuth.azureClientIdHelp'
        },
        {
            key: 'domainDB',
            label: 'OAuth.domainDB',
            type: 'text',
            required: true,
            placeholder: 'OAuth.domainDBPlaceholder',
            order: 5
        },
        {
            key: 'domainDBUser',
            label: 'OAuth.domainDBUser',
            type: 'text',
            required: false,
            placeholder: 'OAuth.domainDBUserPlaceholder',
            order: 6
        },
        {
            key: 'domainDBPassword',
            label: 'OAuth.domainDBPassword',
            type: 'password',
            required: false,
            placeholder: 'OAuth.domainDBPasswordPlaceholder',
            order: 7
        }
    ];

    constructor(
        public activeModal: NgbActiveModal,
        private fb: FormBuilder,
        private service: SystemDomainService,
        private toastService: ToastService,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.loadConfig();
    }

    private initForm(): void {
        const group: any = {};
        this.fields.forEach(field => {
            const validators = field.required ? [Validators.required] : [];
            group[field.key] = ['', validators];
        });
        this.oauthForm = this.fb.group(group);
    }

    loadConfig(): void {
        this.loading = true;
        this.service.getOAuthIntegration(this.domainId).subscribe({
            next: (response) => {
                if (response?.success && response.result) {
                    const config = response.result;
                    this.existingMappingId = config.mappingId || null;
                    this.oauthForm.patchValue({
                        integrationName: config.integrationName,
                        description: config.description,
                        azureTenantId: config.azureTenantId,
                        azureClientId: config.azureClientId,
                        domainDB: config.domainDB,
                        domainDBUser: config.domainDBUser,
                        domainDBPassword: config.domainDBPassword === '********' ? '' : config.domainDBPassword
                    });
                } else if (this.databaseName) {
                    // Pre-fill domainDB with database name from domain if no config exists
                    this.oauthForm.patchValue({
                        domainDB: this.databaseName
                    });
                }
                this.loading = false;
            },
            error: (error) => {
                console.error('Failed to load OAuth config:', error);
                this.toastService.show({
                    type: 'error',
                    data: [this.translate.instant('OAuth.loadConfigError')]
                });
                this.loading = false;
            }
        });
    }

    testConnection(): void {
        if (this.oauthForm.invalid) {
            Object.keys(this.oauthForm.controls).forEach(key => {
                this.oauthForm.get(key)?.markAsTouched();
            });
            return;
        }

        this.testing = true;
        this.testResult = null;

        const formValue = this.oauthForm.value;
        const testRequest = {
            azureTenantId: formValue.azureTenantId,
            azureClientId: formValue.azureClientId,
            domainDB: formValue.domainDB,
            domainDBUser: formValue.domainDBUser,
            domainDBPassword: formValue.domainDBPassword
        };

        this.service.testOAuthConnection(this.domainId, testRequest).subscribe({
            next: (response) => {
                if (response?.result) {
                    this.testResult = response.result;
                    const messageType = this.testResult.overallSuccess ? 'success' : 'warning';
                    const message = this.testResult.overallSuccess
                        ? this.translate.instant('OAuth.testSuccess')
                        : this.translate.instant('OAuth.testPartialSuccess');
                    
                    this.toastService.show({
                        type: messageType,
                        data: [message]
                    });
                }
                this.testing = false;
            },
            error: (error) => {
                console.error('Failed to test OAuth connection:', error);
                this.toastService.show({
                    type: 'error',
                    data: [this.translate.instant('OAuth.testError')]
                });
                this.testing = false;
            }
        });
    }

    saveConfig(): void {
        if (this.oauthForm.invalid) {
            Object.keys(this.oauthForm.controls).forEach(key => {
                this.oauthForm.get(key)?.markAsTouched();
            });
            return;
        }

        this.saving = true;
        const formValue = this.oauthForm.value;

        const request = {
            mappingId: this.existingMappingId,
            domainID: this.domainId,
            integrationName: formValue.integrationName,
            description: formValue.description,
            azureTenantId: formValue.azureTenantId,
            azureClientId: formValue.azureClientId,
            domainDB: formValue.domainDB,
            domainDBUser: formValue.domainDBUser,
            domainDBPassword: formValue.domainDBPassword,
            isActive: true
        };

        this.service.saveOAuthIntegration(this.domainId, request).subscribe({
            next: (response) => {
                if (response?.success) {
                    this.toastService.show({
                        type: 'success',
                        data: [this.translate.instant('OAuth.saveConfigSuccess')]
                    });
                    this.activeModal.close('saved');
                } else {
                    this.toastService.showApiError(response?.errorMessage, this.translate.instant('OAuth.saveConfigError'));
                }
                this.saving = false;
            },
            error: (error) => {
                console.error('Failed to save OAuth config:', error);
                this.toastService.show({
                    type: 'error',
                    data: [this.translate.instant('OAuth.saveConfigError')]
                });
                this.saving = false;
            }
        });
    }

    cancel(): void {
        this.activeModal.dismiss('cancel');
    }
}
